import { ROUTES } from '@/utils/constants';
import { useState, useEffect } from 'react';
import {
  XCircle,
  CheckCircle,
  LinkSimple,
  ArrowRight,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { SocialIcon } from '@/components/shared/SocialIcon';
import { WhatsAppIcon } from '@/components/shared/WhatsAppIcon';

interface ShareSheetProps {
  open: boolean;
  onClose: () => void;
  url: string;
}

// iOS Safari only exposes navigator.clipboard in a secure (HTTPS) context, and
// even then it can be flaky. Fall back to a selection + execCommand("copy"),
// which is the reliable path on mobile Safari.
function copyToClipboard(text: string): boolean {
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    return true;
  }
  return fallbackCopy(text);
}

function fallbackCopy(text: string): boolean {
  const el = document.createElement('textarea');
  el.value = text;
  el.contentEditable = 'true';
  el.readOnly = false;
  el.style.position = 'fixed';
  el.style.top = '0';
  el.style.opacity = '0';
  document.body.appendChild(el);

  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  el.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(el);
  return ok;
}

const SHARE_OPTIONS = [
  {
    name: 'X',
    icon: <SocialIcon icon="x" />,
    action: (url: string) =>
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer',
      ),
  },
  {
    name: 'WhatsApp',
    icon: <WhatsAppIcon />,
    action: (url: string) =>
      window.open(
        `https://wa.me/?text=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer',
      ),
  },
];

export function ShareSheet({ open, onClose, url }: ShareSheetProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(frame);
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!mounted) return null;

  function handleCopy() {
    copyToClipboard(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={`ap-sheet${visible ? ' ap-sheet--open' : ''}`}>
      <button className="ap-sheet__overlay" onClick={onClose} />
      <div className="ap-sheet__panel">
        <div className="ap-sheet__header">
          <span className="ap-sheet__title">Share profile</span>
          <button
            className="ap-sheet__close"
            onClick={onClose}
            aria-label="Close"
          >
            <XCircle size={24} />
          </button>
        </div>

        <div className="ap-sheet__options">
          <button className="ap-sheet__option" onClick={handleCopy}>
            <span className="ap-sheet__option-icon">
              {copied ? (
                <CheckCircle size={18} weight="fill" color="#34c759" />
              ) : (
                <LinkSimple size={18} />
              )}
            </span>
            <span className="ap-sheet__option-name">
              {copied ? 'Copied!' : 'Copy link'}
            </span>
            {!copied && (
              <ArrowRight size={14} className="ap-sheet__option-arrow" />
            )}
          </button>
          {SHARE_OPTIONS.map((opt) => (
            <button
              key={opt.name}
              className="ap-sheet__option"
              onClick={() => opt.action(url)}
            >
              <span className="ap-sheet__option-icon">{opt.icon}</span>
              <span className="ap-sheet__option-name">{opt.name}</span>
              <ArrowRight size={14} className="ap-sheet__option-arrow" />
            </button>
          ))}
        </div>
        <Link to={ROUTES.HOME} className="ap-sheet__claim">
          Want a page like this? Create yours on Nestra for free →
        </Link>
      </div>
    </div>
  );
}
